import React, { useEffect, useRef, useState } from 'react';

import { FiltrosDaLista } from '../../../api/comprasDashboardApi';
import SelecaoMultiplaModal from './SelecaoMultiplaModal';
import { SituacaoDeRequisicao } from '../situacoes';
import '../../../components/modal/contac/filtro/ModalFiltro.css';
import '../../../shared/ui/modal-close-button.css';
import '../pages/DashboardCompras.css';

/**
 * Os filtros DENTRO de uma lista aberta por um card do Dashboard de Compras (transversal/at-90,
 * pedido do Victor, 23/10/2026: "filtros gerais, bem abrangentes").
 *
 * 🔴 Eles só ESTREITAM a lista: o recorte do card continua valendo, e o servidor compõe os dois em
 * `AND`. Por isso a legenda do modal mostra "N de M" — o M é o número do card.
 *
 * Segue o padrão de filtros do sistema: montagem condicional (o pai faz `{aberto && <Modal/>}`),
 * estado local que só sai no "Aplicar", e recebe o que está aplicado — sem isso ele reabre em
 * branco e um "Aplicar" seguinte limparia tudo em silêncio (`BUG-V43`). Sem resumo na tela.
 *
 * ⚠️ Abre SOBRE o modal da lista: o pai trava o próprio fechamento enquanto este existe
 * (`travarFechamento` do `ModalDeDetalhe`), e o z-index do `.modal-filtro-overlay` (9998) já fica
 * acima do overlay da lista (1050/1150). O modal de situações, um nível acima, usa `empilhado`.
 */

/** Os campos que cada lista oferece — nem toda entidade tem todos (requisição não tem fornecedor). */
export type CampoDaLista =
  | 'busca'
  | 'situacoes'
  | 'placa'
  | 'comprador'
  | 'solicitante'
  | 'fornecedor'
  | 'oficina'
  | 'periodo';

interface Props {
  campos: CampoDaLista[];
  /** Obrigatório quando `campos` inclui `situacoes`. */
  situacoes?: SituacaoDeRequisicao[];
  /** O que a busca procura, na frase do placeholder ("número do pedido ou placa"). */
  rotuloDaBusca?: string;
  /** "Emissão do pedido" ou "Cadastro da requisição". */
  rotuloDoPeriodo?: string;
  aplicados: FiltrosDaLista;
  onAplicar: (filtros: FiltrosDaLista) => void;
  onFechar: () => void;
}

/** Quantos filtros estão aplicados — o número do botão "Filtros". */
export function contarFiltrosDaLista(f: FiltrosDaLista): number {
  return [
    f.listaBusca,
    f.listaSituacoes?.length ? 'x' : undefined,
    f.listaPlaca,
    f.listaComprador,
    f.listaSolicitante,
    f.listaFornecedor,
    f.listaOficina,
    f.listaDe || f.listaAte ? 'x' : undefined,
  ].filter(Boolean).length;
}

/** O botão "Filtros" do cabeçalho das listas — com a contagem quando há filtro aplicado. */
export function BotaoFiltrosDaLista({ aplicados, onClick }: { aplicados: FiltrosDaLista; onClick: () => void }) {
  const n = contarFiltrosDaLista(aplicados);
  return (
    <button
      type="button"
      className={`dcr-botao-filtros${n ? ' dcr-botao-filtros--ativo' : ''}`}
      onClick={onClick}
    >
      {n ? `Filtros (${n})` : 'Filtros'}
    </button>
  );
}

export default function FiltroDaListaModal({
  campos,
  situacoes = [],
  rotuloDaBusca = 'número ou placa',
  rotuloDoPeriodo = 'Período',
  aplicados,
  onAplicar,
  onFechar,
}: Props) {
  const [busca, setBusca] = useState(aplicados.listaBusca ?? '');
  const [escolhidas, setEscolhidas] = useState<string[]>(aplicados.listaSituacoes ?? []);
  const [placa, setPlaca] = useState(aplicados.listaPlaca ?? '');
  const [comprador, setComprador] = useState(aplicados.listaComprador ?? '');
  const [solicitante, setSolicitante] = useState(aplicados.listaSolicitante ?? '');
  const [fornecedor, setFornecedor] = useState(aplicados.listaFornecedor ?? '');
  const [oficina, setOficina] = useState(aplicados.listaOficina ?? '');
  const [de, setDe] = useState(aplicados.listaDe ?? '');
  const [ate, setAte] = useState(aplicados.listaAte ?? '');
  const [situacoesAberto, setSituacoesAberto] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // 🔴 Com o modal de situações aberto por cima, os dois caminhos ficam inertes: ele é
    // renderizado fora do `ref`, e o clique nele fecharia este (a armadilha da at-58).
    if (situacoesAberto) return undefined;
    const aoClicarFora = (evento: MouseEvent) => {
      if (ref.current?.contains(evento.target as Node)) return;
      onFechar();
    };
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onFechar();
    };
    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [onFechar, situacoesAberto]);

  const tem = (c: CampoDaLista) => campos.includes(c);
  const texto = (v: string) => v.trim() || undefined;

  const aplicar = () =>
    onAplicar({
      listaBusca: tem('busca') ? texto(busca) : undefined,
      // ⚠️ Lista vazia vira undefined: "nenhuma marcada" é "todas".
      listaSituacoes: tem('situacoes') && escolhidas.length ? escolhidas : undefined,
      listaPlaca: tem('placa') ? placa.toUpperCase().replace(/[^A-Z0-9]/g, '') || undefined : undefined,
      listaComprador: tem('comprador') ? texto(comprador) : undefined,
      listaSolicitante: tem('solicitante') ? texto(solicitante) : undefined,
      listaFornecedor: tem('fornecedor') ? texto(fornecedor) : undefined,
      listaOficina: tem('oficina') ? texto(oficina) : undefined,
      listaDe: tem('periodo') ? de || undefined : undefined,
      listaAte: tem('periodo') ? ate || undefined : undefined,
    });

  const rotuloDasSituacoes =
    escolhidas.length === 0
      ? 'Todas as situações'
      : escolhidas.length === 1
        ? situacoes.find((s) => s.valor === escolhidas[0])?.rotulo ?? escolhidas[0]
        : `${escolhidas.length} situações selecionadas`;

  const campoDeTexto = (rotulo: string, valor: string, mudar: (v: string) => void, exemplo: string) => (
    <label>
      {rotulo}
      <input
        type="text"
        className="premium-text-input"
        aria-label={rotulo}
        placeholder={exemplo}
        value={valor}
        maxLength={60}
        onChange={(e) => mudar(e.target.value)}
      />
    </label>
  );

  return (
    <div className="modal-filtro-overlay">
      <div className="modal-filtro-content" ref={ref}>
        <div className="filtro-modal-card" role="dialog" aria-modal="true" aria-label="Filtros da lista">
          <div className="filtro-modal-header">
            <h2>Filtros da lista</h2>
            <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
              ×
            </button>
          </div>

          <div className="filtro-modal-body">
            {tem('busca') ? (
              <section className="filtro-section">
                <h3>Busca</h3>
                <input
                  type="text"
                  className="premium-text-input"
                  aria-label="Busca"
                  placeholder={`Ex.: ${rotuloDaBusca}`}
                  value={busca}
                  maxLength={60}
                  onChange={(e) => setBusca(e.target.value)}
                />
                <p className="filtro-field-hint">
                  Procura por {rotuloDaBusca}. Com várias palavras, todas precisam aparecer, em
                  qualquer ordem. Acento e maiúsculas não importam.
                </p>
              </section>
            ) : null}

            {tem('situacoes') ? (
              <section className="filtro-section">
                <h3>Situação</h3>
                <button
                  type="button"
                  className="compras-filtro-botao"
                  aria-label="Situação"
                  onClick={() => setSituacoesAberto(true)}
                >
                  {rotuloDasSituacoes}
                </button>
                <p className="filtro-field-hint">Nada marcado: todas as situações do recorte.</p>
                {situacoesAberto ? (
                  <SelecaoMultiplaModal
                    titulo="Situações"
                    empilhado
                    opcoes={situacoes.map((s) => ({ valor: s.valor, rotulo: s.rotulo }))}
                    selecionados={escolhidas}
                    onAplicar={(valores) => {
                      setEscolhidas(valores);
                      setSituacoesAberto(false);
                    }}
                    onFechar={() => setSituacoesAberto(false)}
                  />
                ) : null}
              </section>
            ) : null}

            {tem('placa') || tem('oficina') ? (
              <section className="filtro-section">
                <h3>Veículo e oficina</h3>
                <div className="filtro-grid-2">
                  {tem('placa') ? campoDeTexto('Placa', placa, setPlaca, 'KVV9G43') : null}
                  {tem('oficina') ? campoDeTexto('Oficina', oficina, setOficina, 'parte do nome') : null}
                </div>
                <p className="filtro-field-hint">
                  Placa e oficina vêm do texto da requisição e não estão em todas — o que não tem o
                  dado sai da lista quando o filtro é usado.
                </p>
              </section>
            ) : null}

            {tem('comprador') || tem('solicitante') || tem('fornecedor') ? (
              <section className="filtro-section">
                <h3>Pessoas e fornecedor</h3>
                <div className="filtro-grid-2">
                  {tem('comprador') ? campoDeTexto('Comprador', comprador, setComprador, 'parte do nome') : null}
                  {tem('solicitante')
                    ? campoDeTexto('Solicitante', solicitante, setSolicitante, 'parte do nome')
                    : null}
                  {tem('fornecedor')
                    ? campoDeTexto('Fornecedor', fornecedor, setFornecedor, 'parte do nome')
                    : null}
                </div>
                <p className="filtro-field-hint">Basta parte do nome, e cada palavra vale por si; acento e maiúsculas não importam.</p>
              </section>
            ) : null}

            {tem('periodo') ? (
              <section className="filtro-section">
                <h3>{rotuloDoPeriodo}</h3>
                <div className="filtro-grid-2">
                  <label>
                    De
                    <input
                      type="date"
                      className="premium-text-input"
                      aria-label="De"
                      value={de}
                      onChange={(e) => setDe(e.target.value)}
                    />
                  </label>
                  <label>
                    Até
                    <input
                      type="date"
                      className="premium-text-input"
                      aria-label="Até"
                      value={ate}
                      onChange={(e) => setAte(e.target.value)}
                    />
                  </label>
                </div>
                <p className="filtro-field-hint">
                  Dentro do período do painel: estreita a lista, não amplia.
                </p>
              </section>
            ) : null}
          </div>

          <div className="filtro-modal-footer">
            <button
              type="button"
              className="filtro-btn-secondary filtro-btn-limpar"
              onClick={() => {
                setBusca('');
                setEscolhidas([]);
                setPlaca('');
                setComprador('');
                setSolicitante('');
                setFornecedor('');
                setOficina('');
                setDe('');
                setAte('');
              }}
            >
              Limpar filtros
            </button>
            <button type="button" className="filtro-btn-secondary" onClick={onFechar}>
              Cancelar
            </button>
            <button type="button" className="filtro-btn-primary" onClick={aplicar}>
              Aplicar Filtros
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
